import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import type { ApiClientError } from "./_errors";
import { apiJson } from "./_http";
import type {
	BillingCycle,
	CheckoutRequest,
	CheckoutResponse,
	PortalResponse,
	SubscriptionRow,
} from "../types";

// TanStack Query hooks for the Stripe subscription surface. All HTTP traffic
// flows through the web-app's own Pages Functions at /api/stripe/* and
// /api/me/subscription — never direct to Stripe's API from the browser —
// so the secret key + webhook secret stay server-side. The Pages Functions
// then proxy onto the billing services-cf Worker via the BILLING service
// binding (web-app/wrangler.toml).
//
// We no longer gate behind a `VITE_STRIPE_ENABLED` flag — the live-mode flip
// is a Wrangler-secret rotation on the billing Worker (sk_test_... →
// sk_live_...), not a frontend env. The Worker handles the missing-config
// case by returning a 500 with a `STRIPE_PRICE_* unset` message that the
// usual ApiClientError surfacing renders as inline error text.

// --- Query keys --------------------------------------------------------------

export const subscriptionKeys = {
	all: ["subscription"] as const,
	me: () => [...subscriptionKeys.all, "me"] as const,
};

// --- Hooks -------------------------------------------------------------------

// useMySubscription returns the current user's `subscriptions` row mapped to
// camelCase. Returns `{ status: "none", ... }` (with the rest of the fields
// nulled) when the user has no row — the UI branches on `status === "none"`
// to render the free-tier upgrade CTA.
export function useMySubscription(): UseQueryResult<SubscriptionRow> {
	return useQuery({
		queryKey: subscriptionKeys.me(),
		queryFn: () => apiJson<SubscriptionRow>("/api/me/subscription"),
		staleTime: 30_000,
	});
}

// useStartCheckout posts to /api/stripe/checkout to create a Checkout Session
// then hard-redirects to the Stripe-hosted Checkout URL. We use the URL-based
// flow (not @stripe/stripe-js redirectToCheckout) because Stripe itself has
// recommended the URL path since SDK v6.
//
// `plan` is bound at hook-call time (one mutation per card) so the component
// just calls `.mutate()` with no args. The Worker maps plan → price id.
export function useStartCheckout(
	plan: BillingCycle,
): UseMutationResult<CheckoutResponse, ApiClientError, void> {
	return useMutation({
		mutationFn: async () => {
			const body: CheckoutRequest = { plan };
			const res = await apiJson<CheckoutResponse>("/api/stripe/checkout", {
				method: "POST",
				body: JSON.stringify(body),
			});
			window.location.href = res.checkoutUrl;
			return res;
		},
	});
}

// usePortalRedirect posts to /api/stripe/portal to mint a Customer Portal
// URL, then hard-redirects. Invalidates the subscription query on settle so
// the next visit to /app/settings/billing reflects any plan change made in
// the portal (downgrade, cancel, switch cycle).
export function usePortalRedirect(): UseMutationResult<
	PortalResponse,
	ApiClientError,
	void
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const res = await apiJson<PortalResponse>("/api/stripe/portal", {
				method: "POST",
				body: JSON.stringify({}),
			});
			window.location.href = res.portalUrl;
			return res;
		},
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: subscriptionKeys.me() });
		},
	});
}
